import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Sélecteur de packs dans le panier (Vitalience).
 *
 * Nos produits se vendent en paliers « 1 Flacon / 2 Flacons / 3 Flacons », chaque
 * palier ayant son propre prix remisé. Le compteur natif du panier multipliait le
 * flacon seul (2 x 29,90 €) au lieu de basculer sur le pack de 2 (53,90 €) : le
 * client payait plus cher que le pack affiché sur la fiche.
 *
 * Ici, sur une ligne de panier qui appartient à un palier, le + et le - pilotent
 * le palier lui-même. Le nombre affiché est le nombre de flacons de la ligne
 * (taille du pack x quantité), jamais un nombre de packs.
 *
 *   1 flacon  --+-->  pack de 2  --+-->  pack de 3  --+-->  2 packs de 3 (6 flacons)
 *             <--                <--                <--
 */

const ROOT = window.Shopify?.routes?.root ?? '/';
const PACK = /^\s*(\d+)\s*flacons?\s*$/i;

/** @type {Map<string, Promise<any>>} */
const productCache = new Map();

const getJSON = async (url, init) => {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(`${url} → ${response.status}`);
  return response.json();
};

const getProduct = (handle) => {
  if (!productCache.has(handle)) {
    productCache.set(
      handle,
      getJSON(`${ROOT}products/${handle}.js`).catch(() => null)
    );
  }
  return productCache.get(handle);
};

/**
 * Construit l'échelle des paliers d'un produit, triée par taille croissante.
 * Les paliers en rupture sont écartés : on ne propose pas une marche impossible.
 * @returns {Array<{size: number, id: number, price: number}> | null}
 */
function ladderOf(product) {
  if (!product || !Array.isArray(product.variants)) return null;
  const steps = [];
  for (const variant of product.variants) {
    if (variant.available === false) continue;
    const label = [variant.option1, variant.option2, variant.option3].find((o) => o && PACK.test(o));
    if (!label) continue;
    steps.push({ size: parseInt(PACK.exec(label)[1], 10), id: variant.id, price: variant.price });
  }
  if (steps.length < 2) return null;
  return steps.sort((a, b) => a.size - b.size);
}

const minusIcon = `<span class="visually-hidden">Retirer un flacon</span><span class="svg-wrapper icon-minus"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg></span>`;

/**
 * Le thème rend une corbeille dès que la quantité de ligne vaut 1. Sur un palier
 * la quantité vaut toujours 1, donc la corbeille s'afficherait même quand le bouton
 * redescend d'un cran. On rétablit le vrai geste.
 */
function paintMinus(selector, stepsDown) {
  const minus = selector.querySelector('button[name="remove"], button[name="minus"]');
  if (!minus) return;
  const wanted = stepsDown ? 'minus' : 'remove';
  if (minus.dataset.vitIcon === wanted) return;
  minus.dataset.vitIcon = wanted;
  if (stepsDown && minus.querySelector('.icon-remove')) {
    minus.classList.remove('cart-items__remove');
    minus.innerHTML = minusIcon;
  }
}

/** Le compteur affiche des flacons, pas des packs : on le verrouille en lecture. */
function paintCount(selector, flacons) {
  const input = selector.querySelector('input');
  if (!input) return;
  input.value = String(flacons);
  input.readOnly = true;
  input.setAttribute('aria-label', `${flacons} flacon${flacons > 1 ? 's' : ''} dans le panier`);
}

const money = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')}€`;

/**
 * Prix barré de la ligne, calculé comme sur la fiche produit : le même nombre de
 * flacons achetés à l'unité. Ce n'est pas un ancien prix, c'est le montant que le
 * client paierait vraiment en prenant des flacons seuls, qui restent en vente.
 */
function paintReference(selector, ladder, index, quantity) {
  const cell = selector.closest('.cart-items__price');
  if (!cell) return;

  cell.querySelector('.vit-ref')?.remove();

  const unit = ladder[0];
  if (!unit || index === 0) return;

  const reference = unit.price * ladder[index].size * quantity;
  const paid = ladder[index].price * quantity;
  if (!(reference > paid)) return;

  const struck = document.createElement('s');
  struck.className = 'vit-ref';
  struck.textContent = money(reference);
  struck.setAttribute('aria-label', `au lieu de ${money(reference)} en flacons seuls`);
  cell.prepend(struck);
}

/**
 * Le thème écoute les clics en capture sur `document`, via une délégation qui se
 * déclenche sur l'attribut `on:click`. Cette écoute est posée à l'import de son
 * module, donc avant la nôtre : l'arrêter depuis un gestionnaire concurrent est
 * perdu d'avance. On débranche donc la délégation elle-même sur les lignes de
 * paliers, en déplaçant l'attribut. Même chose pour le `on:blur` du champ, qui
 * renverrait le nombre de flacons affiché comme s'il s'agissait de packs.
 */
function unbindTheme(selector) {
  const targets = selector.querySelectorAll('[on\\:click], [on\\:blur], [on\\:focus]');
  for (const element of targets) {
    for (const attribute of ['on:click', 'on:blur', 'on:focus']) {
      const value = element.getAttribute(attribute);
      if (value === null) continue;
      element.setAttribute(`data-vit-${attribute.replace(':', '-')}`, value);
      element.removeAttribute(attribute);
    }
  }
}

/**
 * Prépare chaque ligne de panier : sans cette lecture faite d'avance, le clic
 * devrait attendre un aller-retour réseau et le thème aurait déjà agi.
 */
async function decorate() {
  const selectors = document.querySelectorAll('cart-quantity-selector-component[data-variant-id]');
  if (!selectors.length) return;

  let cart;
  try {
    cart = await getJSON(`${ROOT}cart.js`);
  } catch {
    return;
  }

  const inCart = new Map(cart.items.map((item) => [item.variant_id, item.quantity]));

  for (const selector of selectors) {
    const variantId = Number(selector.dataset.variantId);
    const line = cart.items.find((item) => item.variant_id === variantId);
    if (!line) {
      delete selector.dataset.vitPack;
      continue;
    }
    const ladder = ladderOf(await getProduct(line.handle));
    const index = ladder ? ladder.findIndex((step) => step.id === variantId) : -1;
    if (index < 0) {
      delete selector.dataset.vitPack;
      continue;
    }
    selector.dataset.vitPack = JSON.stringify({
      ladder,
      index,
      quantity: line.quantity,
      inCart: Object.fromEntries(inCart),
    });
    unbindTheme(selector);
    paintCount(selector, ladder[index].size * line.quantity);
    paintMinus(selector, index > 0 || line.quantity > 1);
    paintReference(selector, ladder, index, line.quantity);
  }
}

/**
 * Décide de la marche suivante.
 * @returns {{from: number, to: number|null, quantity: number}}
 */
function plan(state, direction) {
  const { ladder, index, quantity } = state;
  const current = ladder[index];

  if (direction > 0) {
    // On ne grimpe l'échelle que depuis un pack unique : au-delà, on empile des packs.
    if (quantity === 1 && index < ladder.length - 1) {
      return { from: current.id, to: ladder[index + 1].id, quantity: 1 };
    }
    return { from: current.id, to: current.id, quantity: quantity + 1 };
  }

  if (quantity > 1) return { from: current.id, to: current.id, quantity: quantity - 1 };
  if (index > 0) return { from: current.id, to: ladder[index - 1].id, quantity: 1 };
  return { from: current.id, to: null, quantity: 0 };
}

async function apply(selector, state, step) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

  /** @type {Record<string, number>} */
  const updates = {};
  if (step.to === null) {
    updates[step.from] = 0;
  } else if (step.to === step.from) {
    updates[step.from] = step.quantity;
  } else {
    updates[step.from] = 0;
    // Si le palier visé est déjà dans le panier, on fusionne au lieu d'écraser.
    updates[step.to] = (state.inCart?.[step.to] ?? 0) + step.quantity;
  }

  const cart = await getJSON(`${ROOT}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  for (const id of sectionIds) {
    const html = cart.sections?.[id];
    if (html) await morphSection(id, html, { mode: 'full' });
  }

  document.dispatchEvent(
    new CartUpdateEvent(cart, 'vitalience-packs', {
      itemCount: cart.item_count,
      source: 'vitalience-packs',
      sections: cart.sections,
    })
  );

  await decorate();
}

function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('button');
  if (!button) return;

  const selector = button.closest('cart-quantity-selector-component');
  if (!selector?.dataset.vitPack) return;

  const name = button.getAttribute('name');
  const direction = name === 'plus' ? 1 : name === 'minus' || name === 'remove' ? -1 : 0;
  if (!direction) return;

  event.preventDefault();

  if (selector.dataset.vitBusy) return;
  selector.dataset.vitBusy = '1';

  const state = JSON.parse(selector.dataset.vitPack);
  apply(selector, state, plan(state, direction))
    .catch((error) => {
      console.error('[vitalience-packs]', error);
      window.location.reload();
    })
    .finally(() => {
      delete selector.dataset.vitBusy;
    });
}

document.addEventListener('click', onClick, true);
document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'vitalience-packs') return;
  decorate();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', decorate, { once: true });
} else {
  decorate();
}
